import { useRouter } from 'next/router';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import type { ParsedUrlQuery } from 'querystring';
import { Loading } from '@fagmobler/ui';
import type { Breadcrumb } from '@fagmobler/types';
import {
  getNavigation,
  populateArticlePageWithUrls,
} from '@/lib/sanity/queries';
import { configuredSanityClient } from '@/lib/sanity/sanityClient';
import ArticlePage from '@/cataloguePages/articlePage';
import { PreviewSuspense } from '@sanity/preview-kit';
import dynamic from 'next/dynamic';
import { siteUrl } from '@/next-sitemap.config';
import {
  MaterializedArticlePage,
  articlePageProjection,
} from '@fagmobler/sanity';
const ArticlePagePreview = dynamic(
  () => import('@/cataloguePages/articlePage/preview'),
  {
    ssr: false,
  }
);

export interface Params extends ParsedUrlQuery {
  slugs?: string | string[];
}

export const getPathsInspirasjon = async () => {
  return configuredSanityClient
    .fetch<{ slug: { current?: string } | null; _updatedAt: string }[]>(
      `*[_type == "page" && category == "inspirasjon" && defined(slug.current) && !(_id in path("drafts.**"))] {
        slug,
        _updatedAt
    }`
    )
    .then((docs) => {
      return docs.map((doc) => {
        if (doc.slug?.current) {
          return {
            path: `/inspirasjon/${doc.slug.current}`,
            slug: doc.slug.current,
            _updatedAt: doc._updatedAt,
          };
        }
      });
    });
};

const InspirasjonArticlePage = (
  props: InferGetStaticPropsType<typeof getStaticProps>
) => {
  const { commonProps = {}, data } = props;

  const router = useRouter();
  if (router.isFallback) {
    return <Loading fullscreen />;
  }

  if (commonProps.preview) {
    return (
      <PreviewSuspense fallback={<Loading />}>
        <ArticlePagePreview data={data} {...commonProps} />
      </PreviewSuspense>
    );
  }

  return <ArticlePage data={data} {...commonProps} />;
};

export default InspirasjonArticlePage;

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      // {
      //   params: {
      //     slugs: ['produkter'],
      //   },
      // },
    ],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const navigation = await getNavigation();
  const { preview = false, previewData = null, params } = context;

  const breadcrumbs: Breadcrumb[] = [
    {
      name: 'Inspirasjon',
      path: '/inspirasjon',
    },
  ];

  if (!params?.slug) {
    return {
      revalidate: 60 * 60,
      notFound: true,
    };
  }

  const materializedArticle =
    await configuredSanityClient.fetch<MaterializedArticlePage>(
      `*[_type == "page" && category == "inspirasjon" && slug.current == $slug][0] {
      ${articlePageProjection}
    }`,
      { slug: params.slug }
    );

  const transformedSanityArticlePage =
    (await populateArticlePageWithUrls(materializedArticle)) ||
    materializedArticle ||
    null;

  if (!materializedArticle && !preview) {
    return {
      revalidate: 60 * 60,
      notFound: true,
    };
  }

  const commonProps = {
    preview,
    previewData,
    navigation,
    params: context.params,
  };

  return {
    props: {
      data: {
        canonical: materializedArticle.slug
          ? `${siteUrl}/inspirasjon/${materializedArticle.slug.current}`
          : null,
        transformedSanityArticlePage,
      },
      ...commonProps,
    },
    revalidate: 60 * 60,
  };
};
